// Authentication context provider for database security
// This module provides the current user's authentication details for secure database access

import { getToken, getCurrentUser } from './localAuth';

// Cache for auth context to avoid repeated lookups
let authContextCache: {
  userId: string | null;
  stackAuthId: string | null;
  timestamp: number;
} | null = null;

// Cache duration in milliseconds (30 minutes for better performance)
const CACHE_DURATION = 30 * 60 * 1000;

export interface AuthContext {
  userId: string | null;      // Database user ID
  stackAuthId: string | null; // Stack Auth user ID
}

/**
 * Get current authentication context for database operations
 * This includes both the Stack Auth ID and the database user ID
 */
export async function getCurrentAuthContext(): Promise<AuthContext | null> {
  try {
    // Check cache validity
    if (authContextCache && authContextCache.timestamp > Date.now() - CACHE_DURATION) {
      return {
        userId: authContextCache.userId,
        stackAuthId: authContextCache.stackAuthId
      };
    }

    // Get local auth user from token
    const token = getToken();
    if (!token) {
      authContextCache = null;
      return null;
    }
    const me = await getCurrentUser();
    if (!me || !me.id) {
      // Clear cache if no user
      authContextCache = null;
      return null;
    }

    // Cache the user information
    authContextCache = {
      userId: me.id, // same as stack id for local auth
      stackAuthId: me.id,
      timestamp: Date.now()
    };

    // Return the auth context
    return {
      userId: me.id,
      stackAuthId: me.id
    };
  } catch (error) {
    console.error('Error getting auth context:', error);
    // Return cached value if available, even if expired
    if (authContextCache) {
      console.warn('Using stale auth context due to error');
      return {
        userId: authContextCache.userId,
        stackAuthId: authContextCache.stackAuthId
      };
    }
    return null;
  }
}

/**
 * Clear the authentication context cache
 * Call this when user logs out or authentication state changes
 */
export function clearAuthContextCache(): void {
  authContextCache = null;
}

/**
 * Set auth context manually (useful for testing or initialization)
 */
export function setAuthContext(userId: string | null, stackAuthId: string): void {
  authContextCache = {
    userId,
    stackAuthId,
    timestamp: Date.now()
  };
}

/**
 * Update just the userId in the cache (useful after fetching from database)
 */
export function updateAuthContextUserId(userId: string): void {
  if (authContextCache && authContextCache.stackAuthId) {
    authContextCache.userId = userId;
    // Don't update timestamp - keep the original cache expiry
  }
}
